import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiFamily, ApiChild, FamilyMember, MemberRole } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { useFamilyStore } from '@/stores/familyStore'

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcAge(dateOfBirth: string): number {
  const today = new Date()
  const dob = new Date(dateOfBirth)
  let age = today.getFullYear() - dob.getFullYear()
  const m = today.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--
  return age
}

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Owner',
  contributor: 'Contributor',
  viewer: 'Viewer',
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Spinner
function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-birch-200 border-t-birch-600" />
    </div>
  )
}

// Section card wrapper
function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-6 py-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

// ── Create Family ─────────────────────────────────────────────────────────────

function CreateFamilySection() {
  const qc = useQueryClient()
  const { setFamily } = useFamilyStore()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { mutate, isPending } = useMutation({
    mutationFn: () => api.families.create({ name: name.trim() }),
    onSuccess: (family) => {
      setFamily({ id: family.id, name: family.name, children: [] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setError(null)
    mutate()
  }

  return (
    <div className="mx-auto max-w-md space-y-6 py-12 text-center">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Create your family</h2>
        <p className="mt-1 text-sm text-gray-500">
          You'll be set as the owner and can add children and members.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 text-left">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Family name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="The Smith Family"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            required
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={isPending || !name.trim()}
          className="w-full rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
        >
          {isPending ? 'Creating…' : 'Create family'}
        </button>
      </form>
    </div>
  )
}

// ── Family Name Section ───────────────────────────────────────────────────────

function FamilyNameSection({
  family,
  familyId,
  isOwner,
}: {
  family: ApiFamily
  familyId: string
  isOwner: boolean
}) {
  const qc = useQueryClient()
  const { setFamily } = useFamilyStore()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(family.name)
  const [error, setError] = useState<string | null>(null)

  const { mutate, isPending } = useMutation({
    mutationFn: () => api.families.update(familyId, { name: name.trim() }),
    onSuccess: (updated) => {
      qc.setQueryData<ApiFamily>(['family', familyId], updated)
      setFamily({ id: updated.id, name: updated.name, children: [] })
      setEditing(false)
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setError(null)
    mutate()
  }

  if (editing) {
    return (
      <Section title="Family">
        <form onSubmit={handleSubmit} className="flex items-center gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            required
            autoFocus
          />
          <button
            type="submit"
            disabled={isPending}
            className="rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setName(family.name) }}
            className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </Section>
    )
  }

  return (
    <Section title="Family">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-lg font-medium text-gray-900">{family.name}</p>
          <p className="text-xs text-gray-400">
            Created {new Date(family.createdAt).toLocaleDateString()}
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => setEditing(true)}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            Edit name
          </button>
        )}
      </div>
    </Section>
  )
}

// ── Children Section ──────────────────────────────────────────────────────────

function ChildRow({
  child,
  familyId,
  isOwner,
}: {
  child: ApiChild
  familyId: string
  isOwner: boolean
}) {
  const qc = useQueryClient()
  const { updateChild: storeUpdateChild, removeChild: storeRemoveChild } = useFamilyStore()
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState(child.firstName)
  const [dateOfBirth, setDateOfBirth] = useState(child.dateOfBirth ?? '')
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: () =>
      api.children.update(familyId, child.id, {
        firstName: firstName.trim(),
        dateOfBirth: dateOfBirth || null,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<ApiChild[]>(['children', familyId], (prev) =>
        prev?.map((c) => (c.id === updated.id ? updated : c)) ?? [updated],
      )
      storeUpdateChild(child.id, updated)
      setEditing(false)
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: remove, isPending: removing } = useMutation({
    mutationFn: () => api.children.delete(familyId, child.id),
    onSuccess: () => {
      qc.setQueryData<ApiChild[]>(['children', familyId], (prev) =>
        prev?.filter((c) => c.id !== child.id) ?? [],
      )
      storeRemoveChild(child.id)
    },
    onError: (err: Error) => setError(err.message),
  })

  if (editing) {
    return (
      <li className="rounded-lg border border-birch-200 bg-birch-50 p-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); save() }}
          className="space-y-3"
        >
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                First name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                required
                autoFocus
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Date of birth
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setFirstName(child.firstName); setDateOfBirth(child.dateOfBirth ?? '') }}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
      <div>
        <p className="font-medium text-gray-900">{child.firstName}</p>
        {child.dateOfBirth ? (
          <p className="text-xs text-gray-500">
            Age {calcAge(child.dateOfBirth)} · Born{' '}
            {new Date(child.dateOfBirth).toLocaleDateString()}
          </p>
        ) : (
          <p className="text-xs text-gray-400">No date of birth recorded</p>
        )}
      </div>
      {isOwner && (
        <div className="flex items-center gap-2">
          {confirmDelete ? (
            <>
              <span className="text-xs text-gray-500">Remove {child.firstName}?</span>
              <button
                onClick={() => remove()}
                disabled={removing}
                className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {removing ? 'Removing…' : 'Yes, remove'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                Edit
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
              >
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function ChildrenSection({
  children,
  familyId,
  isOwner,
}: {
  children: ApiChild[]
  familyId: string
  isOwner: boolean
}) {
  const qc = useQueryClient()
  const { addChild: storeAddChild } = useFamilyStore()
  const [adding, setAdding] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { mutate: create, isPending } = useMutation({
    mutationFn: () =>
      api.children.create(familyId, {
        firstName: firstName.trim(),
        dateOfBirth: dateOfBirth || null,
      }),
    onSuccess: (child) => {
      qc.setQueryData<ApiChild[]>(['children', familyId], (prev) => [
        ...(prev ?? []),
        child,
      ])
      storeAddChild(child)
      setFirstName('')
      setDateOfBirth('')
      setAdding(false)
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <Section title="Children">
      {children.length === 0 && !adding && (
        <p className="text-sm text-gray-500">No children added yet.</p>
      )}

      {children.length > 0 && (
        <ul className="mb-4 space-y-2">
          {children.map((child) => (
            <ChildRow key={child.id} child={child} familyId={familyId} isOwner={isOwner} />
          ))}
        </ul>
      )}

      {isOwner && (
        <>
          {adding ? (
            <form
              onSubmit={(e) => { e.preventDefault(); setError(null); create() }}
              className="space-y-3 rounded-lg border border-dashed border-birch-300 bg-birch-50 p-4"
            >
              <p className="text-sm font-medium text-birch-800">Add a child</p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    First name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Claire"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Date of birth
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isPending || !firstName.trim()}
                  className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
                >
                  {isPending ? 'Adding…' : 'Add child'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setFirstName(''); setDateOfBirth('') }}
                  className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="rounded-lg border border-dashed border-birch-300 px-4 py-2 text-sm text-birch-600 hover:bg-birch-50"
            >
              + Add child
            </button>
          )}
        </>
      )}
    </Section>
  )
}

// ── Members Section ───────────────────────────────────────────────────────────

function MemberRow({
  member,
  familyId,
  isOwner,
  isCurrentUser,
}: {
  member: FamilyMember
  familyId: string
  isOwner: boolean
  isCurrentUser: boolean
}) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const { mutate: changeRole, isPending: changingRole } = useMutation({
    mutationFn: (role: MemberRole) =>
      api.members.updateRole(familyId, member.userId, { role }),
    onSuccess: (updated) => {
      qc.setQueryData<FamilyMember[]>(['members', familyId], (prev) =>
        prev?.map((m) => (m.userId === updated.userId ? updated : m)) ?? [updated],
      )
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: remove, isPending: removing } = useMutation({
    mutationFn: () => api.members.remove(familyId, member.userId),
    onSuccess: () => {
      qc.setQueryData<FamilyMember[]>(['members', familyId], (prev) =>
        prev?.filter((m) => m.userId !== member.userId) ?? [],
      )
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {member.firstName} {member.lastName}
            {isCurrentUser && (
              <span className="ml-2 rounded bg-birch-100 px-1.5 py-0.5 text-xs font-medium text-birch-700">
                you
              </span>
            )}
          </p>
          <p className="text-xs text-gray-400">
            Joined {new Date(member.joinedAt).toLocaleDateString()}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {isOwner && !isCurrentUser ? (
            <select
              value={member.role}
              onChange={(e) => changeRole(e.target.value as MemberRole)}
              disabled={changingRole}
              className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500 disabled:opacity-50"
            >
              <option value="owner">Owner</option>
              <option value="contributor">Contributor</option>
              <option value="viewer">Viewer</option>
            </select>
          ) : (
            <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
              {ROLE_LABELS[member.role]}
            </span>
          )}

          {isOwner && !isCurrentUser && (
            confirmRemove ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => remove()}
                  disabled={removing}
                  className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {removing ? 'Removing…' : 'Confirm'}
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRemove(true)}
                className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
              >
                Remove
              </button>
            )
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </li>
  )
}

function MembersSection({
  members,
  familyId,
  isOwner,
  currentUserId,
}: {
  members: FamilyMember[]
  familyId: string
  isOwner: boolean
  currentUserId: string | undefined
}) {
  const qc = useQueryClient()
  const [inviting, setInviting] = useState(false)
  const [email, setEmail] = useState('')
  const [invFirstName, setInvFirstName] = useState('')
  const [invLastName, setInvLastName] = useState('')
  const [role, setRole] = useState<MemberRole>('contributor')
  const [error, setError] = useState<string | null>(null)

  const { mutate: invite, isPending } = useMutation({
    mutationFn: () =>
      api.members.invite(familyId, {
        email: email.trim(),
        firstName: invFirstName.trim(),
        lastName: invLastName.trim(),
        role,
      }),
    onSuccess: (member) => {
      qc.setQueryData<FamilyMember[]>(['members', familyId], (prev) => [
        ...(prev ?? []),
        member,
      ])
      setEmail('')
      setInvFirstName('')
      setInvLastName('')
      setRole('contributor')
      setInviting(false)
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <Section title="Members">
      <ul className="mb-4 space-y-2">
        {members.map((m) => (
          <MemberRow
            key={m.userId}
            member={m}
            familyId={familyId}
            isOwner={isOwner}
            isCurrentUser={m.userId === currentUserId}
          />
        ))}
      </ul>

      {isOwner && (
        <>
          {inviting ? (
            <form
              onSubmit={(e) => { e.preventDefault(); setError(null); invite() }}
              className="space-y-3 rounded-lg border border-dashed border-birch-300 bg-birch-50 p-4"
            >
              <p className="text-sm font-medium text-birch-800">Invite a member</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    First name *
                  </label>
                  <input
                    type="text"
                    value={invFirstName}
                    onChange={(e) => setInvFirstName(e.target.value)}
                    placeholder="Carol"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Last name *
                  </label>
                  <input
                    type="text"
                    value={invLastName}
                    onChange={(e) => setInvLastName(e.target.value)}
                    placeholder="Smith"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="carol@example.com"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Role *
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as MemberRole)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
                  >
                    <option value="owner">Owner — full access</option>
                    <option value="contributor">Contributor — log activities</option>
                    <option value="viewer">Viewer — read only</option>
                  </select>
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isPending || !email.trim() || !invFirstName.trim() || !invLastName.trim()}
                  className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
                >
                  {isPending ? 'Sending invite…' : 'Send invite'}
                </button>
                <button
                  type="button"
                  onClick={() => { setInviting(false); setError(null) }}
                  className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-400">
                If the person doesn't have an account yet, they'll receive a
                temporary password by email.
              </p>
            </form>
          ) : (
            <button
              onClick={() => setInviting(true)}
              className="rounded-lg border border-dashed border-birch-300 px-4 py-2 text-sm text-birch-600 hover:bg-birch-50"
            >
              + Invite member
            </button>
          )}
        </>
      )}
    </Section>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function FamilyPage() {
  const { user } = useAuthStore()

  // 1. Get profile → learn familyId
  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
  })

  const familyId = profile?.familyId ?? null

  // 2. Family details (only once we know the id)
  const { data: family, isLoading: loadingFamily } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => api.families.get(familyId!),
    enabled: !!familyId,
  })

  // 3. Children
  const { data: children = [], isLoading: loadingChildren } = useQuery({
    queryKey: ['children', familyId],
    queryFn: () => api.children.list(familyId!),
    enabled: !!familyId,
  })

  // 4. Members
  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['members', familyId],
    queryFn: () => api.members.list(familyId!),
    enabled: !!familyId,
  })

  const myRole = members.find((m) => m.userId === user?.userId)?.role
  const isOwner = myRole === 'owner'

  const isLoading =
    loadingProfile ||
    (!!familyId && (loadingFamily || loadingChildren || loadingMembers))

  // ── No-family state ──────────────────────────────────────────────────────
  if (!isLoading && !familyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Family</h1>
        <CreateFamilySection />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Family</h1>

      {isLoading && <Spinner />}

      {!isLoading && family && (
        <>
          <FamilyNameSection
            family={family}
            familyId={familyId!}
            isOwner={isOwner}
          />
          <ChildrenSection
            children={children}
            familyId={familyId!}
            isOwner={isOwner}
          />
          <MembersSection
            members={members}
            familyId={familyId!}
            isOwner={isOwner}
            currentUserId={user?.userId}
          />
        </>
      )}
    </div>
  )
}
